// Libs //
import { mapGetters } from 'vuex';

// Constants //
import { MESSAGE_TYPE } from '../../shared/constants/messages';

// Helpers //
import { buildFlowContextIds } from '../helper/keystoneFlowHelper';

export const AI_SUGGEST_PROVIDER = {
  KEYSTONE: 'keystone',
  CHATBOT: 'chatbot',
};

export const MESSAGE_ROLE = {
  USER: 'user',
  ASSISTANT: 'assistant',
};

const CONTACT_ATTRIBUTES = ['email', 'phone_number', 'identifier'];

const CONVERSATION_CUSTOM_ATTRIBUTES = [
  'booking_reference',
  'threadId',
  'bookingId',
  'bookingNr',
  'bookingIdExtern',
  'arrival',
  'departure',
  'listingName',
  'categoryId',
  'updated_at',
  'booking_property_id',
  'booking_conversation_id',
  'accountId',
  'categoryDetails',
  'roomDetails',
];

const CONVERSATION_CUSTOM_ATTRIBUTES_KEYS = {
  accountId: 'airbnbAccountId',
};

export default {
  computed: {
    ...mapGetters({
      aiNests: 'chatbot/getAINests',
      keystoneFlows: 'management/getKeystoneFlows',
      managementUiFlags: 'management/getUiFlags',
      chatbotUiFlags: 'chatbot/getUiFlags',
    }),

    isSuggestOptionsFetching() {
      return (
        this.$store.getters['management/getUiFlags'].isKeystoneFlowsFetching ||
        this.$store.getters['chatbot/getUiFlags'].isAINestLoading
      );
    },

    allSuggestOptions() {
      return [
        ...this.keystoneFlows.map(this.mapKeystoneFlowToNormalizedStructure),
        ...this.aiNests.map(this.mapChatbotNestToNormalizedStructure),
      ];
    },
  },

  methods: {
    // Mappers //
    mapKeystoneFlowToNormalizedStructure(flow) {
      const { id, name, flowId, inboxId, updatedAt, ...other } = flow;
      return {
        feId: `${AI_SUGGEST_PROVIDER.KEYSTONE}:${id}`,
        id,
        name,
        executeId: flowId,
        provider: AI_SUGGEST_PROVIDER.KEYSTONE,
        inboxId: inboxId ?? null,
        updatedAt: updatedAt ?? null,
        other,
      };
    },

    mapChatbotNestToNormalizedStructure(nest) {
      const { id, nestId, title, ...other } = nest;
      return {
        feId: `${AI_SUGGEST_PROVIDER.CHATBOT}:${id}`,
        id,
        name: title,
        executeId: nestId,
        provider: AI_SUGGEST_PROVIDER.CHATBOT,
        other,
      };
    },

    // Getters //
    getSuggestOptionById(id, provider) {
      return this.allSuggestOptions.find((option) =>
        provider ? option.id === id && option.provider === provider : option.id === id
      );
    },

    getSuggestOptionByExecuteId(executeId, provider) {
      return this.allSuggestOptions.find((option) =>
        provider
          ? option.executeId === executeId && option.provider === provider
          : option.executeId === executeId
      );
    },

    getSuggestOptionByFeId(feId) {
      return this.allSuggestOptions.find((option) => option.feId === feId);
    },

    getAttributesFromObject(keys, object, keyNames = {}) {
      return keys.reduce((accumulator, currentValue) => {
        const newValue = { ...accumulator };

        if (object[currentValue]) {
          const newKey = keyNames[currentValue] ?? currentValue;

          newValue[newKey] = object[currentValue];
        }

        return newValue;
      }, {});
    },

    preparePayloadForKeystoneFlow({
      incomingMessages,
      history,
      accountId,
      translationLanguage,
      contact = {},
      conversationCustomAttributes = {},
      conversation,
      inbox,
    }) {
      return {
        userPrompt: incomingMessages[incomingMessages.length - 1],
        messageHistory: history.map((message) => ({
          role:
            message.message_type !== MESSAGE_TYPE.INCOMING
              ? MESSAGE_ROLE.ASSISTANT
              : MESSAGE_ROLE.USER,
          content: message.content ?? '',
        })),
        initialContext: {
          accountId,
          translationLanguage:
            translationLanguage || this.$root.$i18n.locale.replace(/_/g, '-'),
          ...buildFlowContextIds({ conversation, inbox, contact }),
          ...this.getAttributesFromObject(CONTACT_ATTRIBUTES, contact),
          ...this.getAttributesFromObject(
            CONVERSATION_CUSTOM_ATTRIBUTES,
            conversationCustomAttributes,
            CONVERSATION_CUSTOM_ATTRIBUTES_KEYS
          ),
        },
      };
    },

    preparePayloadForChatbotFlow({
      incomingMessages,
      accountId,
      guestIdentifier,
      showLanguage = true,
    }) {
      return {
        message: incomingMessages.join(' '),
        accountId,
        guestIdentifier,
        showLanguage,
      };
    },

    executeSuggestionFlow({
      nest,
      accountId,
      incomingMessages,
      history,
      guestIdentifier,
      translationLanguage,
      contact = {},
      conversationCustomAttributes = {},
      conversation,
      inbox,
    }) {
      if (
        !nest ||
        !incomingMessages ||
        incomingMessages.length === 0 ||
        !nest.provider
      ) {
        return null;
      }

      const requestData = {
        provider: nest.provider,
        executeId: nest.executeId,
        payload: {},
      };

      switch (requestData.provider) {
        case AI_SUGGEST_PROVIDER.KEYSTONE:
          requestData.payload = this.preparePayloadForKeystoneFlow({
            incomingMessages,
            history,
            accountId,
            translationLanguage,
            contact,
            conversationCustomAttributes,
            conversation,
            inbox,
          });

          break;

        case AI_SUGGEST_PROVIDER.CHATBOT:
          requestData.payload = this.preparePayloadForChatbotFlow({
            incomingMessages,
            accountId,
            guestIdentifier,
          });

          break;

        default:
          return null;
      }

      return this.$store.dispatch('chatbot/getAiSuggestions', requestData);
    },
  },
};
